import { useState, useEffect, useCallback } from 'react';
import { createWalletClient, custom } from 'viem';

export type DetectedWallet = {
  name: string;
  provider: any;
  rdns?: string;
};

export function useWalletSign() {
  const [detectedWallets, setDetectedWallets] = useState<DetectedWallet[]>([]);
  const [address, setAddress] = useState<string | null>(null);
  const [walletClient, setWalletClient] = useState<any>(null);

  // EIP-6963 wallet discovery — each installed wallet announces itself with a
  // verified rdns identifier, so we don't have to guess from `window.ethereum`.
  useEffect(() => {
    const found = new Map<string, DetectedWallet>();
    const handler = (event: any) => {
      const { info, provider } = event.detail || {};
      if (!info || !provider) return;
      found.set(info.uuid, { name: info.name, provider, rdns: info.rdns });
      setDetectedWallets(Array.from(found.values()));
    };
    window.addEventListener('eip6963:announceProvider', handler as EventListener);
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    return () => {
      window.removeEventListener('eip6963:announceProvider', handler as EventListener);
    };
  }, []);

  const connect = useCallback(async (provider: any) => {
    const client = createWalletClient({ transport: custom(provider) });
    const [addr] = await client.getAddresses();
    if (!addr) {
      await provider.request({ method: 'eth_requestAccounts' });
      const [addrAfter] = await client.getAddresses();
      setAddress(addrAfter ?? null);
    } else {
      setAddress(addr);
    }
    setWalletClient(client);
  }, []);

  const disconnect = useCallback(() => {
    setAddress(null);
    setWalletClient(null);
  }, []);

  const signMessage = useCallback(
    async (message: string): Promise<string> => {
      if (!walletClient || !address) throw new Error('Wallet not connected');
      return walletClient.signMessage({ account: address as `0x${string}`, message });
    },
    [walletClient, address]
  );

  return { detectedWallets, connect, disconnect, address, signMessage };
}
